import { RefObject, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export const SLIDE_WIDTH = 1280;
export const SLIDE_HEIGHT = 720;

// Captures every [data-deck-slide] element inside containerRef into one landscape page each, saves
// the result as fileName, then calls onComplete. Runs once per fileName. `label` only appears in
// error logs (e.g. "school report").
export const useSlideDeckPdf = (
  containerRef: RefObject<HTMLDivElement>,
  fileName: string,
  onComplete: () => void,
  label: string,
) => {
  useEffect(() => {
    let cancelled = false;

    const generate = async () => {
      const container = containerRef.current;
      if (!container) {
        onComplete();
        return;
      }

      await document.fonts.ready;

      const images = Array.from(container.querySelectorAll('img'));
      await Promise.all(
        images.map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              }),
        ),
      );
      await new Promise((resolve) => setTimeout(resolve, 250));

      const slides = Array.from(container.querySelectorAll<HTMLElement>('[data-deck-slide]'));
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [SLIDE_WIDTH, SLIDE_HEIGHT],
        compress: true,
      });

      for (let i = 0; i < slides.length; i++) {
        if (cancelled) return;
        if (i > 0) {
          doc.addPage([SLIDE_WIDTH, SLIDE_HEIGHT], 'landscape');
        }
        try {
          const canvas = await html2canvas(slides[i], {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
          });
          const imageData = canvas.toDataURL('image/png');
          doc.addImage(imageData, 'PNG', 0, 0, SLIDE_WIDTH, SLIDE_HEIGHT, undefined, 'FAST');
        } catch (error) {
          // Don't let a single slide's capture failure abort the whole export - the rest of the
          // deck is still worth having.
          console.error(`Failed to capture slide ${i + 1} of the ${label} PDF`, error);
        }
      }

      if (!cancelled) {
        doc.save(fileName);
        onComplete();
      }
    };

    generate().catch((error) => {
      // Anything that throws outside the per-slide try/catch above (document.fonts.ready
      // rejecting, doc.save failing, etc.) must still release isGeneratingPDF - otherwise
      // SpinnerOverlay stays up forever with no way to dismiss it short of a page reload.
      console.error(`Failed to generate the ${label} PDF`, error);
      if (!cancelled) onComplete();
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileName]);
};
